import { Injectable, Inject, PLATFORM_ID, signal } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  lastValueFrom,
  Observable,
  of,
  tap,
} from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import {
  FrontEndToolPermission,
  LoginBody,
  PermissionAttributes,
  RegisterBody,
  RoleAttributes,
  UserReturn,
} from '../../../server/types';
import { environment } from '../../../environments/environment';
import { RouteService } from './route.service';
import { PamlabApiServiceService } from './pamlab-api-service.service';
import {
  PasswordResetBody,
  RegistrationCodeBody,
} from '../../../server/schemas/auth';
import { ToolListService } from './tool-list.service';
import { HttpService } from './http.service';

export interface UserName {
  last_name: string;
  first_name: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // private ROOT_API = '/v1/api';

  private userSubject = new BehaviorSubject<UserReturn | null>(null);
  user$: Observable<UserReturn | null> = this.userSubject.asObservable();
  private fullPermissionListSubject = new BehaviorSubject<
    PermissionAttributes[]
  >([]);
  fullPermissionList$ = this.fullPermissionListSubject.asObservable();

  private rolesSignal = signal<RoleAttributes[]>([]);

  constructor(
    private routeService: RouteService,
    private apiService: PamlabApiServiceService,
    private toolListService: ToolListService,
    private httpService: HttpService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  login(userCredential: LoginBody): Observable<UserReturn | null> {
    if (!this.userSubject.getValue()) {
      return this.httpService.post<UserReturn>(`/login`, userCredential).pipe(
        tap((user) => {
          this.userSubject.next(user);
          this.setTools(user);
        }),
      );
    } else {
      return this.user$;
    }
  }

  register(data: RegisterBody): Observable<any> {
    return this.httpService.post(`/register`, data);
  }

  loadUser() {
    if (this.userSubject.value) {
      return this.user$;
    }

    return this.httpService.get<UserReturn>(`/users/jwt-user`).pipe(
      tap((user) => {
        this.userSubject.next(user);
        this.setTools(user);
      }),
      catchError(() => {
        this.userSubject.next(null);
        return of(null);
      }),
    );
  }

  getUser() {
    return this.user$;
  }

  /**
   * Determine if a user have access to the tools
   * @param path
   * @returns True if the current have access to the root path, false otherwise
   */
  hasPermission(permissionValue: string, checkActive = false): boolean {
    if (!this.userSubject.value) return false;

    const fullPermissionListValues = this.fullPermissionListSubject.value.map(
      (p) => p.name,
    );

    if (
      (this.userSubject.value.role.name === 'pamlab' ||
        this.userSubject.value.role.name === 'admin') &&
      fullPermissionListValues.includes(permissionValue)
    ) {
      return true;
    }

    if (permissionValue === 'general') return true;

    const userPermissions = this.userSubject.value.permissions;
    const permission = userPermissions.find((x) => x.name === permissionValue);

    if (!permission) {
      return false;
    }

    if (checkActive) {
      return permission.active;
    }

    return true;
  }

  /**
   * Checks if the current logged in user is an admin
   */
  isAdmin() {
    return (
      this.userSubject.value?.role.name === 'pamlab' ||
      this.userSubject.value?.role.name === 'admin'
    );
  }

  getRole(): string | undefined {
    return this.userSubject.value?.role.name;
  }

  getUserId(): number | undefined {
    return this.userSubject.value?.id;
  }

  getRoleTitle(): string | undefined {
    return this.userSubject.value?.role.title;
  }

  saveToken(token: string) {
    isPlatformBrowser(this.platformId) && localStorage.setItem('token', token);
  }

  getUserName(): UserName {
    return {
      first_name: this.userSubject.value?.first_name || '',
      last_name: this.userSubject.value?.last_name || '',
    };
  }

  getStudentID() {
    return this.userSubject.value?.uwf_id || 0;
  }

  getStudentNameAndId() {
    const firstName = this.userSubject.value?.first_name || '';
    const lastName = this.userSubject.value?.last_name || '';
    const id = this.userSubject.value?.uwf_id || 0;

    return `${firstName} ${lastName} (${id})`;
  }

  logout() {
    if (isPlatformBrowser(this.platformId)) {
      this.httpService.getWithRawUrl('/logout').subscribe({
        next: (_res) => {
          this.apiService.resetStudents();
          localStorage.clear();
          this.userSubject.next(null);
          this.routeService.navigate(environment['login_url']);
        },
        error: (err) => {
          console.log(err);
        },
      });
    }
  }

  getToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('token');
    }
    return null;
  }

  fetchUserRoles(): Observable<string[]> {
    return this.httpService.get<string[]>(`/user-roles`);
  }

  saveUserRegistrationCode(payloadBody: RegistrationCodeBody) {
    return this.httpService.post<number>(`/registration-codes`, payloadBody);
  }

  getUserPermissions() {
    return this.userSubject.value?.permissions || [];
  }

  getFullPermissionList() {
    if (
      this.userSubject.value?.role.name === 'pamlab' ||
      this.userSubject.value?.role.name === 'admin'
    ) {
      if (this.fullPermissionListSubject.value.length) {
        return this.fullPermissionList$;
      }

      return this.httpService.get<PermissionAttributes[]>(`/permissions`).pipe(
        tap((permissions) => {
          this.fullPermissionListSubject.next(permissions);
        }),
        catchError(() => {
          this.fullPermissionListSubject.next([]);
          return of(null);
        }),
      );
    }
    return of([]);
  }

  loadRoles() {
    this.httpService.get<RoleAttributes[]>(`/roles`).subscribe({
      next: (roles) => {
        this.rolesSignal.set(roles);
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  getRoles() {
    return this.rolesSignal;
  }

  getDefaultRolePermissionsByRoleId(roleId: number) {
    return this.httpService.get<FrontEndToolPermission[]>(
      `/default-role-permissions/${roleId}`,
    );
  }

  resetPassword(payload: PasswordResetBody) {
    return this.httpService.post(`/password-reset`, payload);
  }

  private setTools(user: UserReturn) {
    this.toolListService.setSuccessMetricsToolList(user.hidden_tools!);
    this.toolListService.setAdminSupportToolList(user.hidden_tools!);
    this.toolListService.setExternalAdminSupportToolList(user.hidden_tools!);
  }
}
